import React, { Fragment, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { CircularProgress, Skeleton } from "@mui/material"
import Grid from "@mui/material/Grid"
import { DateRangePicker, Tooltip } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import moment from "moment"

import { KonanPageHeader } from "../../components/core/KonanPageHeader"
import { MetricsBarChart } from "../../components/graphs/Bar/MetricsBarChart"
import { CoveragePieChart } from "../../components/graphs/CoveragePieChart"
import { ConfusionMatrix } from "../../components/graphs/HeatMap/ConfusionMatrix"
import { AccuracyCard } from "../../components/status/AccuracyCard"
import { KonanEmptyState } from "../../components/utility/KonanEmptyState"
import { useDateQuery } from "../../hooks/useDateQuery"
import { KonanAPI } from "../../services/KonanAPI"
import type { RootState } from "../../store/ReduxStore"
import type { ClassificationEvaluation } from "../../types/generated/api/ClassificationEvaluation"
import type { DeploymentLatestFeedbackDateTime } from "../../types/generated/api/DeploymentLatestFeedbackDateTime"
import type { DeploymentPredictionsSummary } from "../../types/generated/api/DeploymentPredictionsSummary"
import type { ListClassificationModelOutputs } from "../../types/generated/api/ListClassificationModelOutputs"
import type { PaginatedListClassificationModelOutputsList } from "../../types/generated/api/PaginatedListClassificationModelOutputsList"
import { customColorScheme } from "../../utils/genericHelpers"
import {
  convertCoverageToPieChart,
  convertDataToConfusionMatrix,
  getModelPredictionsCoverageState,
  getModelPredictionsGraphsState,
} from "../../utils/modelDetailsHelpers"

import styles from "./ClassificationMetrics.module.scss"

type ParamsType = {
  id: string
}

type CoverageBlockType = {
  name: string
  predictedValue: number
  actualValue: number
  colorIndex: number
}

function CoverageBlock(props: Readonly<CoverageBlockType>): React.ReactElement {
  const { name, predictedValue, actualValue, colorIndex } = props

  return (
    <Grid item>
      <Grid container justifyContent="center" alignItems="center" gap={0.5}>
        <Grid item>
          <span key="chip" className={styles.coverageBlock} style={{ background: customColorScheme()[colorIndex] }} />
        </Grid>
        <Grid item className={styles.coverageBlockName}>
          <strong key="name">{name}</strong>
        </Grid>
        <Grid item className={styles.coverageBlockValueContainer}>
          <span key="value1" className={styles.coverageBlockValue}>
            {predictedValue + " / "}
          </span>
          <span key="value2" className={styles.coverageBlockValue}>
            {actualValue}
          </span>
        </Grid>
      </Grid>
    </Grid>
  )
}

export function ClassificationMetrics(): React.ReactElement {
  const { id: projectId } = useParams<ParamsType>()
  const { t } = useTranslation("ClassificationMetrics")

  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const navigate = useNavigate()

  const [startDate, setStartDate, endDate, setEndDate] = useDateQuery()

  const metricsBaseHeight = 50
  const graphHeight = 300

  const now = moment()

  // used as a check if active config is loading
  // starts out as True to initiate the page in a loading state
  const [isActiveConfigLoading, setIsActiveConfigLoading] = useState<boolean>(true)

  const { data: activeConfiguration } = useQuery<AxiosResponse<ListClassificationModelOutputs>, AxiosError>(
    ["activeConfig", currentModel?.uuid],
    () => KonanAPI.fetchActiveModelConfiguration(currentModel?.uuid as string),
    {
      enabled: !!currentModel?.uuid && !!currentProject?.uuid && currentProject.type !== "generic",
      retry: false,
      onSettled: () => {
        setIsActiveConfigLoading(false)
      },
    },
  )

  // fetch model predictions data
  // labels, confusion matrix, coverage, accuracy, ...
  const { data: evaluationData, isLoading } = useQuery<AxiosResponse<ClassificationEvaluation>, AxiosError>(
    ["classification-evaluation", startDate, endDate, currentModel?.uuid],
    () =>
      KonanAPI.fetchModelClassificationEvaluationMetrics(
        startDate?.toISOString() as string,
        endDate?.toISOString() as string,
        currentModel?.uuid as string,
      ),
    { enabled: !!currentModel?.uuid && !!startDate && !!endDate },
  )

  // fetch predictions in current project since date of creation
  // to check if it has successful predictions since the date of creation or not
  const { isLoading: isApiRequestsLoading, data: apiRequests } = useQuery<AxiosResponse<DeploymentPredictionsSummary>>(
    ["apiRequests", projectId, startDate, endDate],
    () =>
      KonanAPI.fetchApiRequestsSummary({
        project_uuid: projectId as string,
        start_date: currentProject?.created_at as string,
        // TODO:: check the requirements for this condition
        end_date: now?.toISOString(),
      }),
    {
      enabled: projectId !== "" && currentProject?.created_at !== undefined && endDate != null,
    },
  )

  // fetch model features (model predictions table data)
  const { isLoading: isModelPredictionsLoading, data: modelPredictions } = useQuery<
    AxiosResponse<PaginatedListClassificationModelOutputsList>,
    AxiosError
  >(
    ["classification-predictions", startDate, endDate, currentModel?.uuid],
    () =>
      KonanAPI.fetchModelOutputPredictionsData(
        startDate?.toISOString() as string,
        endDate?.toISOString() as string,
        currentModel?.uuid as string,
      ),
    {
      keepPreviousData: true,
      enabled: !!currentModel?.uuid && !!startDate && !!endDate,
    },
  )

  //fetch last feedback in this project
  const { isLoading: isLastFeedbackLoading, data: lastFeedbackResponse } = useQuery<
    AxiosResponse<DeploymentLatestFeedbackDateTime>
  >(["lastFeedback", projectId], () => KonanAPI.fetchLastFeedback(projectId as string), {
    enabled: projectId !== "",
  })

  const isDataLoading = useMemo(() => {
    return isLoading || isModelPredictionsLoading || isLastFeedbackLoading || isApiRequestsLoading
  }, [isApiRequestsLoading, isLastFeedbackLoading, isLoading, isModelPredictionsLoading])

  //sort classification labels to maintain consistency across all cards
  const sortedLabels = useMemo(() => {
    if (evaluationData?.data?.labels?.length) {
      return evaluationData?.data.labels.toSorted((a, b) => a.name.localeCompare(b.name))
    }
    return []
  }, [evaluationData])

  const dynamicGraphHeight = useMemo(() => {
    return metricsBaseHeight + sortedLabels.length * 40
  }, [sortedLabels])

  // check the empty state of the metrics
  const metricsGraphsState = useMemo(() => {
    if (evaluationData?.data && apiRequests?.data && modelPredictions?.data) {
      const hasFeedback =
        lastFeedbackResponse?.data.latest_feedback_date_time !== null &&
        lastFeedbackResponse?.data.latest_feedback_date_time !== undefined &&
        lastFeedbackResponse?.data.latest_feedback_date_time !== ""

      return getModelPredictionsGraphsState(
        apiRequests?.data.successful ?? 0,
        modelPredictions.data.count ?? 0,
        evaluationData.data.observations_count,
        evaluationData.data.feedbacks_count,
        hasFeedback,
      )
    }
  }, [apiRequests, evaluationData, lastFeedbackResponse, modelPredictions])

  // check the empty state of the coverage
  const coverageGraphState = useMemo(() => {
    if (evaluationData?.data && apiRequests?.data && modelPredictions?.data) {
      return getModelPredictionsCoverageState(
        apiRequests?.data.successful ?? 0,
        modelPredictions.data.count ?? 0,
        evaluationData.data.observations_count,
      )
    }
  }, [evaluationData, modelPredictions, apiRequests])

  // convert confusion matrix and labels into nivo heatmap format
  const confusionMatrixData = useMemo(() => {
    if (evaluationData?.data.confusion_matrix.length) {
      return convertDataToConfusionMatrix(evaluationData?.data.confusion_matrix ?? [], sortedLabels)
    }
    return []
  }, [evaluationData, sortedLabels])

  // convert classification labels into coverage pie chart (nivo format)
  const coveragePieChartData = useMemo(() => {
    if (sortedLabels.length && evaluationData?.data) {
      return convertCoverageToPieChart(sortedLabels, evaluationData?.data?.observations_count ?? 1)
    }
    return []
  }, [evaluationData, sortedLabels])

  return (
    <Grid container spacing={2}>
      <KonanPageHeader
        title={t("ClassificationMetrics:title")}
        subtitle={
          <React.Fragment>
            {t("ClassificationMetrics:subtitle.line1")}
            <br />
            {t("ClassificationMetrics:subtitle.line2")}
          </React.Fragment>
        }
        actions={[
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            disableFuture
            key={"metrics-date-picker"}
          />,
        ]}
      />

      {isActiveConfigLoading ? (
        <Grid item className={"empty-container"}>
          <CircularProgress size={36} />
        </Grid>
      ) : !activeConfiguration ? (
        <Grid item xs={12} className={"empty-container"}>
          <KonanEmptyState
            setAction={() =>
              navigate(
                `/projects/${projectId}/models/configurations${currentModel?.uuid ? `?model_id=${currentModel?.uuid}` : ""}`,
              )
            }
            title={t("ClassificationMetrics:empty.title")}
            subTitle={t("ClassificationMetrics:empty.subtitle")}
            buttonText={t("ClassificationMetrics:empty.action")}
          />
        </Grid>
      ) : (
        <Fragment>
          <Grid item xs={12}>
            <Grid container alignItems={"center"} gap={1}>
              {isDataLoading ? (
                <Grid item>
                  <Skeleton animation="wave" width={320} height={18} />
                </Grid>
              ) : !isDataLoading && sortedLabels && sortedLabels.length > 0 ? (
                <Fragment>
                  {sortedLabels.map((item, index) => (
                    <CoverageBlock
                      key={item.name}
                      name={item.name}
                      predictedValue={item.predicted_occurances}
                      actualValue={item.actual_occurances}
                      colorIndex={index % customColorScheme().length}
                    />
                  ))}

                  <Grid item>
                    <Tooltip
                      title={t("ClassificationMetrics:predictionsVsActuals")}
                      placement="top"
                      display="auto"
                      verticalAlign="middle"
                    >
                      <InfoOutlinedIcon fontSize="inherit" style={{ fill: "var(--base-text-1)" }} />
                    </Tooltip>
                  </Grid>
                </Fragment>
              ) : (
                ""
              )}
            </Grid>
          </Grid>

          {/* Model Output section */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={3}>
                <AccuracyCard
                  accuracy={metricsGraphsState?.showGraphs ? (evaluationData?.data.accuracy ?? null) : null}
                  isLoading={isDataLoading || !!(startDate && !endDate)}
                  cardHeight={graphHeight}
                  emptyState={metricsGraphsState}
                  range={endDate?.diff(startDate, "days") ? endDate?.diff(startDate, "days") + 1 : undefined}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <CoveragePieChart
                  title={t("graphs.coverage")}
                  data={coverageGraphState?.showGraphs ? coveragePieChartData : []}
                  totalCoveragePercentage={evaluationData?.data.coverage ?? 0}
                  graphHeight={graphHeight}
                  isLoading={isDataLoading || !!(startDate && !endDate)}
                  emptyState={coverageGraphState}
                  range={endDate?.diff(startDate, "days") ? endDate?.diff(startDate, "days") + 1 : undefined}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <ConfusionMatrix
                  title={t("graphs.confusionMatrix")}
                  data={metricsGraphsState?.showGraphs ? confusionMatrixData : []}
                  graphHeight={graphHeight}
                  isLoading={isDataLoading || !!(startDate && !endDate)}
                  emptyState={metricsGraphsState}
                  range={endDate?.diff(startDate, "days") ? endDate?.diff(startDate, "days") + 1 : undefined}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <MetricsBarChart
                  title={t("graphs.f1")}
                  data={metricsGraphsState?.showGraphs ? sortedLabels : []}
                  graphKey={"f1_score"}
                  graphHeight={
                    sortedLabels && sortedLabels.length && metricsGraphsState?.showGraphs
                      ? dynamicGraphHeight
                      : graphHeight
                  }
                  isLoading={isDataLoading || !!(startDate && !endDate)}
                  emptyState={metricsGraphsState}
                  range={endDate?.diff(startDate, "days") ? endDate?.diff(startDate, "days") + 1 : undefined}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <MetricsBarChart
                  title={t("graphs.precision")}
                  data={metricsGraphsState?.showGraphs ? sortedLabels : []}
                  graphKey={"precision"}
                  graphHeight={
                    sortedLabels && sortedLabels.length && metricsGraphsState?.showGraphs
                      ? dynamicGraphHeight
                      : graphHeight
                  }
                  isLoading={isDataLoading || !!(startDate && !endDate)}
                  emptyState={metricsGraphsState}
                  range={endDate?.diff(startDate, "days") ? endDate?.diff(startDate, "days") + 1 : undefined}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <MetricsBarChart
                  title={t("graphs.recall")}
                  data={metricsGraphsState?.showGraphs ? sortedLabels : []}
                  graphKey={"recall"}
                  graphHeight={
                    sortedLabels && sortedLabels.length && metricsGraphsState?.showGraphs
                      ? dynamicGraphHeight
                      : graphHeight
                  }
                  isLoading={isDataLoading || !!(startDate && !endDate)}
                  emptyState={metricsGraphsState}
                  range={endDate?.diff(startDate, "days") ? endDate?.diff(startDate, "days") + 1 : undefined}
                />
              </Grid>
            </Grid>
          </Grid>
        </Fragment>
      )}
    </Grid>
  )
}
